import React, { useEffect, useState } from "react";

import PageSideBar from "components/PageSideBar";
import { AdminReferral } from "models/admin/AdminReferral";

import styles from "./Admin.module.scss";
import AdminService from "api/Admin/AdminService";
import { Delete } from "lucide-react";
import classNames from "classnames";
import TextField from "components/TextField";

const Admin = () => {
  const [referrals, setReferrals] = useState<AdminReferral[]>([]);

  const [label, setLabel] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [mailTo, setMailTo] = useState<string>("");

  const [error, setError] = useState<string>();

  const getReferralData = async () => {
    try {
      const response = await AdminService.getAdminReferrals();
      if (response.referrals === undefined) {
        return;
      }

      setReferrals(response.referrals);
    } catch (err) {
      setError("Something went wrong on GET");
    }
  };

  useEffect(() => {
    getReferralData();
  }, []);

  const onReferralDelete = (referral_id: number) => {
    try {
      AdminService.deleteAdminReferral(referral_id);
    } catch (err) {
      setError("Something went wrong on DELETE");
    } finally {
      getReferralData();
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onReferralCreate = () => {
    if (label.length <= 0 || code.length <= 0 || mailTo.length <= 0) {
      setError("Empty fields...");
      return;
    }

    if (!validateEmail(mailTo)) {
      setError("Invalid email format");
      return;
    }

    try {
      AdminService.createAdminReferral(label, code, mailTo);
    } catch (err) {
      setError("Something went wrong on CREATE");
    } finally {
      getReferralData();
    }
  };

  return (
    <div className={styles.page}>
      <PageSideBar />
      <div className={styles.content}>
        <div className={styles.title}>Admin Panel</div>

        <div className={classNames(styles.section, styles.referralSection)}>
          <div className={styles.header}>Referral System</div>
          <div className={styles.content}>
            {referrals.length > 0 && (
              <table className={styles.referralTable}>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Referral Code</th>
                    <th>Mail To</th>
                    <th>Currency</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral) => (
                    <tr key={referral.code}>
                      <td>{referral.label}</td>
                      <td>{referral.code}</td>
                      <td>{referral.mail_to}</td>
                      <td>
                        {new Date(referral.created_timestamp).toLocaleString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )}
                      </td>
                      <td>
                        <Delete
                          className={styles.deleteButton}
                          onClick={() => onReferralDelete(referral.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {referrals.length <= 0 && <div>No Referrals...</div>}

            <div className={styles.separator} />

            <div className={styles.form}>
              <div className={styles.formTitle}>Create</div>
              <TextField label="Label" value={label} setValue={setLabel} />
              <TextField label="Code" value={code} setValue={setCode} />
              <TextField label="Mail To" value={mailTo} setValue={setMailTo} />
              <button className={styles.button} onClick={onReferralCreate}>
                Create Referral
              </button>
            </div>

            {error && <div className={styles.error}>{error}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
